import React, { useEffect, useState } from 'react';

/**
 * Time Component
 * 
 * This component displays the current time and updates it at a specified interval.
 * It also features a flashing effect for the colons between hours, minutes, and seconds.
 * 
 * @component
 * @memberof Components 
 * 
 * @param {Object} props - React component props.
 * @param {number} [props.flashInterval=1000] - The interval (in milliseconds) at which the time updates and the flashing effect toggles.
 * @param {string} [props.fontSize='1.7vw'] - The font size of the displayed time.
 * @param {string} [props.color='#464E5F'] - The text color of the time display.
 * @param {string} [props.fontWeight='lighter'] - The font weight of the displayed time.
 * @param {string} [props.textAlign='center'] - The text alignment of the displayed time.
 * @param {string} [props.fontFamily='Roboto, sans-serif'] - The font family of the displayed time.
 * 
 * @returns {JSX.Element} A formatted current time display with a flashing effect.
 * 
 * @example
 * // Default usage
 * <Time />
 * 
 * @example
 * // Custom styles and update interval
 * <Time flashInterval={500} fontSize="2rem" color="blue" fontWeight="bold" textAlign="left" fontFamily="Arial" />
 */
const Time = ({ flashInterval = 1000, fontSize = '1.7vw', color = '#464E5F', fontWeight = 'lighter', textAlign = 'center', fontFamily = 'Roboto, sans-serif' }) => {
    const [time, setTime] = useState(new Date());
    const [flash, setFlash] = useState(true);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setTime(new Date());
            setFlash((prevFlash) => !prevFlash);
        }, flashInterval); // Custom interval passed as a prop

        return () => clearInterval(intervalId);
    }, [flashInterval]);

    /**
     * Formats the given date into a time string with flashing colons.
     * 
     * @param {Date} date - The date object to format.
     * @returns {string} A formatted time string in HH:MM:SS format with flashing colons.
     */
    const formatTime = (date) => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${hours}${flash ? ':' : ' '}${minutes}${flash ? ':' : ' '}${seconds}`;
    };

    return (
        <div>
            <h1 style={{ fontSize, color, fontWeight, textAlign, fontFamily }}>
                {formatTime(time)}
            </h1>
        </div>
    );
};

export default Time;